import React, { useContext, useState, MouseEvent, useEffect } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import { Survey as OriginalSurvey } from '@cbreone/core-ui/dist/types';
import { useTranslation } from 'react-i18next';
import CommentsContext from '../../hooks/CommentsContext';
import InteractiveSurveyContext from '../../hooks/InteractiveSurveyContext';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    btnOn: {
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '4px',
      backgroundColor: `${theme.palette.primary.main}`,
      boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',
      color: 'white',

      padding: '3px 16px',
      fontWeight: 500,

      marginLeft: '14px',
      marginRight: '8px',

      '& p': {
        display: 'inline',
        margin: 0,
        padding: '0 4px',
      },
      '& svg': {
        width: 17,
        height: 15,
        marginTop: 6,
        fill: 'white',
      },
    },
    btnOff: {
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '4px',
      backgroundColor: 'white',
      color: `${theme.palette.primary.main}`,
      boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',

      padding: '3px 16px',
      fontWeight: 500,

      marginLeft: '14px',
      marginRight: '8px',

      '& p': {
        display: 'inline',
        margin: 0,
        padding: '0 4px',
      },
      '& svg': {
        width: 17,
        height: 15,
        marginTop: 6,
        fill: 'none',
        stroke: `${theme.palette.primary.main}`,
      },
    },
    btnOffShortList: {
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '4px',
      backgroundColor: `${theme.palette.primary.main}`,
      color: 'white',
      boxShadow: '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 1px 5px 0px rgba(0, 0, 0, 0.12)',

      padding: '3px 16px',
      fontWeight: 500,

      marginLeft: '14px',
      marginRight: '8px',

      '& p': {
        display: 'inline',
        margin: 0,
        padding: '0 4px',
      },
      '& svg': {
        width: 17,
        height: 15,
        marginTop: 6,
        fill: 'none',
        stroke: `${theme.palette.primary.main}`,
      },
    },
    btnHover: {
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px solid ${theme.palette.primary.main}`,
      borderRadius: '4px',
      backgroundColor: 'hsl(156, 14%, 93%)',
      color: `${theme.palette.primary.main}`,

      padding: '3px 16px',
      fontWeight: 500,

      marginLeft: '14px',
      marginRight: '8px',
      cursor: 'pointer',

      '& p': {
        display: 'inline',
        margin: 0,
        padding: '0 4px',
      },
      '& svg': {
        width: 17,
        height: 15,
        marginTop: 6,
        fill: 'none',
        stroke: `${theme.palette.primary.main}`,
      },

      '@media (max-width: 767px)': {
        minWidth: '100%',
        height: '100%',
        borderRadius: 0,
        '& p': { display: 'none' },
      },
    },
    shortlistSvg: { marginTop: 0, flexShrink: 0 },
    shortlistIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
    shortlistText: { verticalAlign: 'middle', margin: 0 },
    shortListBtnContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: '120px',
      minHeight: '36px',
    },
  }));

interface Survey extends OriginalSurvey {
  isSurveyShortlistEnabled: boolean;
}
interface SLProps {
  prop: {
    isClientShortlist: boolean;
  };
  onShortlistClick: Function;
}

export const ShortlistBtn = (props: SLProps) => {
  const { prop, onShortlistClick } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [isHover, setIsHover] = useState(false);

  const { survey } = useContext(InteractiveSurveyContext);
  const { isSurveyShortlistEnabled: isShortlistEnabled } = survey as unknown as Survey;
  const getButtonClass = () => {
    if (isHover) return classes.btnHover;
    if (prop.isClientShortlist) return classes.btnOn;
    return classes.btnOffShortList;
  };

  const internalStateClass = getButtonClass();
  const onSLClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onShortlistClick();
    setIsHover(false);
  };
  if (isShortlistEnabled === null) return null;
  return (
    <button
      onClick={onSLClick}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      className={`${internalStateClass} ${classes.shortListBtnContainer}`}
      id={prop.isClientShortlist ? 'SC-Shortlisted-Button' : 'SC-Shortlist-Button'}
    >
      <svg width="17" height="15" viewBox="0 0 17 15" style={{ marginTop: 0, flexShrink: 0 }}>
      {prop.isClientShortlist ? (
        <CheckIcon className={classes.shortlistIcon}/>
      ) : (
        <g />
      )}
      </svg>
      <p className={classes.shortlistText}>{props.prop.isClientShortlist ? t('propertyList.shortlisted') : t('propertyList.shortlist')}</p>
    </button>
  );
};

interface CommentProps {
  myId?: string;
}

export const CommentBtn: React.FC<CommentProps> = ({ myId }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    setCommentPanelStatus,
    setCommPropId,
    propsWithComments,
    isClientCommentEnabled,
    timeLeft,
  }: {
    setCommentPanelStatus: Function,
    setCommPropId: Function,
    propsWithComments: string[],
    isClientCommentEnabled: boolean,
    timeLeft: number,
  } = useContext(CommentsContext);
  const [isHover, setIsHover] = useState(false);
  const [hasComments, setHasComments] = useState(false);
  const getButtonClass = () => {
    if (isHover) return classes.btnHover;
    if (hasComments) return classes.btnOn;
    return classes.btnOff;
  };

  const internalStateClass = getButtonClass();
  const commentsClicked = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    if (timeLeft === 5) {
      if (myId) setCommPropId(myId);
      setCommentPanelStatus(true);
    }
  };

  useEffect(() => {
    if (myId && propsWithComments.includes(myId)) {
      setHasComments(true);
    } else {
      setHasComments(false);
    }
  }, [propsWithComments, myId]);

  const withComments = (
    <svg
      viewBox='-1 -1 16 16'
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M13.5 5C14.8807 5 16 3.88071 16 2.5C16 1.11929 14.8807 0 13.5 0C12.1193 0 11 1.11929 11 2.5C11 3.88071 12.1193 5 13.5 5ZM13.5 6C13.5558 6 13.6112 5.9987 13.6663 5.99612V11.6666C13.6663 12.3999 13.0663 12.9999 12.333 12.9999H2.99967L0.333008 15.6666L0.339674 3.66659C0.339674 2.93325 0.933008 2.33325 1.66634 2.33325H10.0039C10.0013 2.38851 10 2.4441 10 2.5C10 4.433 11.567 6 13.5 6ZM4.99967 8.33325H3.66634V6.99992H4.99967V8.33325ZM7.66634 8.33325H6.33301V6.99992H7.66634V8.33325ZM10.333 8.33325H8.99967V6.99992H10.333V8.33325Z'
      />
    </svg>
  );

  const withoutComments = (
    <svg
      viewBox='-2 -2 15 14'
    >
      <path
        fillRule='evenodd'
        clipRule='evenodd'
        d='M2.00016 8.66667L0.66683 10.3001V0.666667H11.3335V8.66667H2.00016ZM4 4H3V5H4V4ZM6.5 4H5.5V5H6.5V4ZM8 4H9V5H8V4Z'
      />
    </svg>
  );

  return (
    <>
      {
        isClientCommentEnabled && (
             <button
      type="button"
      onClick={commentsClicked}
      onMouseEnter={() => setIsHover(true)}
      onMouseLeave={() => setIsHover(false)}
      className={internalStateClass}
      aria-label={t('comments.openComments')}
    >
      {hasComments ? withComments : withoutComments}
          </button>
        )
      }
    </>
  );
};
